import {
    flexRender,
    getCoreRowModel,
    useReactTable
} from '@tanstack/react-table';
import React from 'react';

/**
 * DynamicTable is a reusable table component that renders dynamic data using `@tanstack/react-table`.
 * It supports customizable columns, styling, and auto-generated headers, rows, and footers.
 *
 * @component
 * @memberof Components
 * @param {Object} props - The properties passed to the DynamicTable component.
 * @param {Array} props.data - The array of data objects to display in the table.
 * @param {Array} props.columns - The array of column definitions used to configure table columns.
 *
 * @example
 * const data = [
 *   { id: 1, name: "John Doe", age: 28 },
 *   { id: 2, name: "Jane Doe", age: 32 }
 * ];
 *
 * const columns = [
 *   {
 *     accessorKey: "id",
 *     header: "ID",
 *     cell: info => info.getValue()
 *   },
 *   {
 *     accessorKey: "name",
 *     header: "Name",
 *     cell: info => info.getValue()
 *   },
 *   {
 *     accessorKey: "age",
 *     header: "Age",
 *     cell: info => info.getValue()
 *   }
 * ];
 *
 * <DynamicTable data={data} columns={columns} />
 *
 * @returns {JSX.Element} The rendered table component.
 */
const DynamicTable = (props) => {

    const table = useReactTable({
        data: props.data,
        columns: props.columns,
        getCoreRowModel: getCoreRowModel ? getCoreRowModel() : "",
    })

    return (
        <table style={{ width: "100%", borderCollapse: 'collapse' }}>
            <thead >
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => {
                            const isLeftAligned = index === 0;
                            const headerStyle = {
                                fontSize: '13px',
                                paddingLeft: isLeftAligned ? '10px' : '0px',
                                textAlign: isLeftAligned ? 'left' : 'center', // Conditionally set the textAlign
                                backgroundColor: 'white',
                                fontWeight: 'bold',
                                // border:'1px solid lightgrey'

                            };
                            return (
                                <th key={header.id} colSpan={header.colSpan} style={headerStyle}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </th>
                            )
                        })}
                    </tr>
                ))}
            </thead>
            <tbody style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {table.getRowModel().rows.map((row, index) => {
                    const isLastRow = index === table.getCoreRowModel().rows.length - 1;
                    const rowStyle = {
                        borderBottom: '1px solid #DFDFDF',
                        borderTop: '1px solid #DFDFDF',
                        fontSize: '13px',
                        textAlign: 'center',
                        fontWeight: isLastRow ? 'bold' : 'normal', // Conditionally set the fontWeight
                        backgroundColor: index % 2 === 0 ? '#d5e5ff' : 'white',
                    };
                    return (
                        <tr key={row.id} style={rowStyle}>
                            {row.getVisibleCells().map((cell, index) => {
                                const isLeftAligned = index === 0;
                                const cellStyle =
                                {
                                    borderBottom: '1px solid #DFDFDF', borderTop: '1px solid #DFDFDF', fontSize: '13px',
                                    textAlign: isLeftAligned ? 'left' : 'center', // Conditionally set the textAlign
                                    paddingLeft: isLeftAligned ? '10px' : '0px',
                                    paddingTop: '5px',
                                    paddingBottom: '5px',
                                }
                                return (
                                    <td key={cell.id} style={cellStyle}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    )
                })}
            </tbody>
            <tfoot>
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id}>
                        {footerGroup.headers.map(header => (
                            <th key={header.id} colSpan={header.colSpan}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.footer,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </table>
    )
}

export default DynamicTable
